/**
 * The ClinikChat mark.
 *
 * Design constraint that drove everything: this has to survive at 16px in a
 * crowded Chrome toolbar and as a small floating button over a dense patient
 * record. That rules out detail — what reads at that size is one bold
 * silhouette plus one counter-form (a hole punched through it).
 *
 * So: a squared speech bubble (the silhouette) with a pulse trace knocked out
 * of it (the counter-form). The bubble says conversation, the pulse says
 * clinical, and the two together say what the previous mark — a featureless
 * oval — did not: this is a clinical conversation, not another consumer
 * messenger. The pulse baseline is set flush with the point the tail springs
 * from so the two read as a single gesture rather than a bubble with a sticker
 * on it.
 *
 * Colours come from the Praxentis master palette (tailwind.config.js) and are
 * not re-invented here: navy #0B163F ground, labs-400 #6EB6FF mark.
 */

interface LogoProps {
  /** Tailwind size classes for the outer chip, e.g. "h-16 w-16". */
  className?: string;
  /** Render only the bubble glyph, no navy chip — for use on a navy surface. */
  bare?: boolean;
}

/** The bubble + pulse glyph on its own, inheriting `currentColor`. */
export function LogoGlyph({ className = 'h-6 w-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      {/* Bubble silhouette. Tail springs from the lower left so the mark leans
          into the reading direction rather than sitting dead centre. */}
      <path
        d="M6.5 4h19A4.5 4.5 0 0 1 30 8.5v11a4.5 4.5 0 0 1-4.5 4.5H14.8l-6.3 4.9a.8.8 0 0 1-1.3-.63V24h-.7A4.5 4.5 0 0 1 2 19.5v-11A4.5 4.5 0 0 1 6.5 4Z"
        fill="currentColor"
      />
      {/* Pulse knocked out of the bubble. Stroked rather than filled so the
          weight stays even when the whole mark is scaled down. */}
      <path
        d="M7.4 14.2h3.9l2.2-4.6 4.1 9.2 2.2-4.6h4.8"
        stroke="var(--logo-knockout, #0B163F)"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** The full lockup: glyph on the navy chip, as used on the auth screens. */
export default function Logo({ className = 'h-16 w-16', bare = false }: LogoProps) {
  if (bare) return <LogoGlyph className={className} />;
  return (
    <div
      className={`flex items-center justify-center rounded-2xl bg-praxentis-navy text-labs-400 shadow-warm-md ${className}`}
    >
      <LogoGlyph className="h-[56%] w-[56%]" />
    </div>
  );
}
